import {
  createContext,
  useContext,
  useState,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import { Outlet, createRoute, useRouterState } from '@tanstack/react-router'

import { BreadcrumbBar, BreadcrumbHost, BreadcrumbProvider } from '@/ui/breadcrumb'
import { useSelectedEntity } from '@/modules/hierarchy'
import {
  activeNavigationItem,
  AreaSidebar,
  navigationHref,
  ProductLayout,
} from '@/app/routes/area-layout'

import { iamGuardRoute } from './iam-guard-route'

const AccessHeaderActionsContext = createContext<HTMLElement | null | undefined>(
  undefined,
)

export function AccessHeaderActions({ children }: { children: ReactNode }) {
  const target = useContext(AccessHeaderActionsContext)

  if (target === undefined) return children
  return target === null ? null : createPortal(children, target)
}

export function AccessLayout() {
  const { entityId, ancestors: ancestorsQuery } = useSelectedEntity()
  const destination = useRouterState({
    select: (state) => activeNavigationItem('access', state.matches.at(-1)?.fullPath),
  })
  const [headerActions, setHeaderActions] = useState<HTMLElement | null>(null)
  const breadcrumb = (ancestorsQuery.data?.items ?? []).map((a) => ({
    label: a.name,
    href: destination ? navigationHref(destination, a.entityId) : undefined,
  }))
  const page =
    destination && entityId
      ? {
          label: destination.label,
          href: navigationHref(destination, entityId),
        }
      : null

  return (
    <BreadcrumbProvider>
      <AccessHeaderActionsContext.Provider value={headerActions}>
        <ProductLayout
          sidebar={<AreaSidebar area="access" />}
          header={
            <BreadcrumbBar className="min-h-11 shrink-0 justify-between gap-4">
              <div className="min-w-0">
                <BreadcrumbHost
                  segments={[...breadcrumb, ...(page ? [page] : [])]}
                />
              </div>
              <div ref={setHeaderActions} className="flex shrink-0 items-center" />
            </BreadcrumbBar>
          }
        >
          <Outlet />
        </ProductLayout>
      </AccessHeaderActionsContext.Provider>
    </BreadcrumbProvider>
  )
}

export const accessRoute = createRoute({
  getParentRoute: () => iamGuardRoute,
  id: 'access',
  component: AccessLayout,
})
